import {Component, Input, OnChanges} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {SubmissionHttpService} from '../data-access/submission-http.service';
import {FormHttpService} from '../data-access/form-http.service';
import {ReviewType, Submission} from '../shared/models/submission.model';
import {
  isReviewHandled,
  payrollReviewClass,
  payrollReviewLabel,
  submissionStatusClass,
  submissionStatusLabel
} from '../shared/services/submission-display';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {Roles} from '../../../_auth/x-enums/roles';

/** One form's submissions for the employee: the current (latest) one, plus any older versions as a stack. */
interface FormSubmissionGroup {
  formId: string;
  title: string;
  latest: Submission;
  older: Submission[];
  payrollRequired: boolean;
  expanded: boolean;
}

/**
 * A manager panel listing **one employee's** form submissions, **grouped by form**. Each group shows the latest
 * submission as the primary row; older versions collapse into an expandable stack beneath it (so re-submitted
 * forms read as a single entry with history rather than a flat list of near-duplicates). Each row links to the
 * privileged {@link SubmissionDetailComponent} (`/company/forms/submissions/{id}`) for masked view + reveal +
 * attachment download under the `SensitiveDataAccessor` role. Embedded in the employee-details "Formulare" tab.
 */
@Component({
  selector: 'forms-employee-submissions-panel',
  standalone: true,
  imports: [DatePipe, NgClass, RouterLink],
  template: `
    @if (loading) {
      <div class="flex items-center justify-center py-12">
        <svg class="h-7 w-7 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
        </svg>
      </div>
    } @else if (groups.length === 0) {
      <p class="text-sm text-gray-500 italic py-8 border-2 border-dashed border-gray-100 rounded-2xl text-center">
        Dieser Mitarbeiter hat noch keine Formulare eingereicht.
      </p>
    } @else {
      <div class="space-y-3">
        @for (g of groups; track g.formId) {
          <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <!-- Current version: the primary, clickable row. -->
            <div [routerLink]="['/company/forms/submissions', g.latest.id]" [state]="{back: backUrl}"
                 class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50">
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-gray-900 truncate">{{ g.title }}</div>
                <div class="text-xs text-gray-500">
                  Aktuell: v{{ g.latest.formVersion }}
                  · {{ g.latest.submittedAt ? (g.latest.submittedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}
                </div>
              </div>
              <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="statusClass(g.latest.status)">{{ statusLabel(g.latest.status) }}</span>
              <div class="flex flex-wrap items-center gap-2" (click)="$event.stopPropagation()">
                @if (g.latest.needsReview) {
                  <span title="{{ g.latest.reviewReason }}" class="rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-semibold text-yellow-700">Prüfung nötig</span>
                }
                <!-- Validity (#404): the server-derived expiry of the current answer. "abgelaufen" replaces the
                     "gültig bis" hint once the form's Gültigkeitsdauer has run out. -->
                @if (g.latest.expired) {
                  <span data-test="expired-badge"
                        class="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">abgelaufen</span>
                } @else if (g.latest.expiresAt) {
                  <span data-test="valid-until"
                        class="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">gültig bis {{ g.latest.expiresAt | date: 'dd.MM.yyyy' }}</span>
                }
                @if (g.payrollRequired) {
                  <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="payrollClass(handled(g.latest))">{{ payrollLabel(handled(g.latest)) }}</span>
                  @if (canHandle) {
                    @if (handled(g.latest)) {
                      <button type="button" (click)="setHandled(g.latest, false)" [disabled]="busy.has(g.latest.id)"
                              class="text-xs text-gray-400 underline hover:text-gray-600 disabled:opacity-50">zurücksetzen</button>
                    } @else {
                      <button type="button" (click)="setHandled(g.latest, true)" [disabled]="busy.has(g.latest.id)"
                              class="text-xs font-semibold text-theme-primary-600 underline hover:text-theme-primary-800 disabled:opacity-50">als erledigt markieren</button>
                    }
                  }
                }
              </div>
              @if (g.older.length) {
                <button type="button" (click)="g.expanded = !g.expanded; $event.stopPropagation()"
                        class="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700">
                  {{ g.older.length }} {{ g.older.length === 1 ? 'ältere Version' : 'ältere Versionen' }}
                  <svg class="h-3.5 w-3.5 transition-transform" [class.rotate-180]="g.expanded" fill="none"
                       viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6"/>
                  </svg>
                </button>
              }
            </div>

            <!-- Older versions: the expandable stack. -->
            @if (g.expanded) {
              <div class="border-t border-gray-100 bg-gray-50/60 divide-y divide-gray-100">
                @for (s of g.older; track s.id) {
                  <div [routerLink]="['/company/forms/submissions', s.id]" [state]="{back: backUrl}"
                       class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 pl-8 cursor-pointer transition-colors hover:bg-gray-100">
                    <div class="min-w-0 flex-1 text-sm text-gray-600">
                      v{{ s.formVersion }} · {{ s.submittedAt ? (s.submittedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}
                    </div>
                    <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="statusClass(s.status)">{{ statusLabel(s.status) }}</span>
                    @if (g.payrollRequired) {
                      <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="payrollClass(handled(s))">{{ payrollLabel(handled(s)) }}</span>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  `
})
export class EmployeeSubmissionsPanelComponent implements OnChanges {
  @Input() employeeId = '';

  protected loading = true;
  /** This employee's submissions grouped by form, latest first. */
  protected groups: FormSubmissionGroup[] = [];
  private titles: Record<string, string> = {};
  /** Form ids (= definition keys) whose forms require a payroll review — only those show the Lohnprüfung badge. */
  private payrollRequired = new Set<string>();
  /** Submission ids with an in-flight handle/unhandle request (disables the button). */
  protected busy = new Set<string>();

  protected readonly statusLabel = submissionStatusLabel;
  protected readonly statusClass = submissionStatusClass;
  protected readonly payrollLabel = payrollReviewLabel;
  protected readonly payrollClass = payrollReviewClass;

  constructor(private http: SubmissionHttpService, private forms: FormHttpService, private auth: AuthenticationService) {
  }

  ngOnChanges(): void {
    if (!this.employeeId) return;
    this.loading = true;
    forkJoin({list: this.http.list(), defs: this.forms.listDefinitions()}).subscribe({
      next: ({list, defs}) => {
        this.titles = Object.fromEntries(defs.map(d => [d.id, d.title]));
        this.payrollRequired = new Set(
          defs.filter(d => d.requiredReviews?.includes(ReviewType.Payroll)).map(d => d.id));
        this.groups = this.groupByForm(list.filter(s => s.employeeId === this.employeeId));
        this.loading = false;
      },
      error: () => {
        this.groups = [];
        this.loading = false;
      }
    });
  }

  /** Group an employee's submissions by form, newest submission first within each group and across groups. */
  private groupByForm(submissions: Submission[]): FormSubmissionGroup[] {
    const byForm = new Map<string, Submission[]>();
    for (const s of submissions) (byForm.get(s.formId) ?? byForm.set(s.formId, []).get(s.formId)!).push(s);

    return [...byForm.entries()]
      .map(([formId, subs]) => {
        const sorted = subs.sort((a, b) => this.time(b.submittedAt) - this.time(a.submittedAt) || b.formVersion - a.formVersion);
        return {
          formId,
          title: this.titles[formId] ?? `Formular ${formId.substring(0, 8)}`,
          latest: sorted[0],
          older: sorted.slice(1),
          payrollRequired: this.payrollRequired.has(formId),
          expanded: false
        };
      })
      .sort((a, b) => this.time(b.latest.submittedAt) - this.time(a.latest.submittedAt));
  }

  /** Whether the current user may mark submissions handled (management / data roles, or the external payroll
   *  accountant whose very job is to review submissions — independent of the sensitive-data toggle). */
  protected get canHandle(): boolean {
    const perms = this.auth.userValue?.permissions ?? [];
    return perms.includes(Roles.PlatformOwner) || perms.includes(Roles.ShardAdmin)
      || perms.includes(Roles.SensitiveDataAccessor) || perms.includes('CompanyOwner')
      || perms.includes(Roles.Payroll);
  }

  /** Whether a submission is already handled for the payroll review. */
  protected handled(s: Submission): boolean {
    return isReviewHandled(s, ReviewType.Payroll);
  }

  /** Mark/clear the payroll-handled state for a submission, updating the row in place. */
  protected setHandled(s: Submission, handled: boolean): void {
    if (this.busy.has(s.id)) return;
    this.busy.add(s.id);
    const op = handled
      ? this.http.markReviewHandled(s.id, ReviewType.Payroll)
      : this.http.markReviewUnhandled(s.id, ReviewType.Payroll);
    op.subscribe({
      next: updated => {
        s.reviews = updated.reviews ?? [];
        this.busy.delete(s.id);
      },
      error: () => this.busy.delete(s.id)
    });
  }

  /** Where the submission detail's back arrow should return to — this employee's details page, on the Formulare tab. */
  protected get backUrl(): string {
    return `/company/employee/details?id=${this.employeeId}&view=submissions`;
  }

  private time(v?: string | Date): number {
    return v ? new Date(v).getTime() : 0;
  }
}
